import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import {
  apiService,
  User,
  SubscriptionInfo,
  SubscriptionResponse,
} from "../services/api";

// Mapeia a resposta de GET /doctor/subscription para o estado de auth, levando
// todos os campos novos (plano, trial, janela de acesso, troca agendada, cartão,
// compromisso) usados pelos guards e pela tela de gestão.
const mapSubscription = (sub: SubscriptionResponse): SubscriptionInfo => ({
  status: sub.status as SubscriptionInfo["status"],
  paymentUrl: null,
  isLifetime: sub.isLifetime,
  planKind: sub.planKind,
  annualBilling: sub.annualBilling,
  isTrial: sub.isTrial,
  trialEndsAt: sub.trialEndsAt,
  accessUntil: sub.accessUntil,
  pendingPlanChange: sub.pendingPlanChange,
  cancelScheduledAt: sub.cancelScheduledAt,
  cardLastDigits: sub.cardLastDigits,
  cardBrand: sub.cardBrand,
  commitment: sub.commitment,
});

interface AuthContextType {
  isAuthenticated: boolean;
  isFirstLogin: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  clearFirstLogin: () => void;
  loading: boolean;
  user: User | null;
  error: string;
  subscription: SubscriptionInfo | null;
  setSubscription: (subscription: SubscriptionInfo | null) => void;
  needsTermsAcceptance: boolean;
  acceptTerms: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string>("");
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(
    null,
  );
  const [needsTermsAcceptance, setNeedsTermsAcceptance] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);

  const checkTermsAcceptance = async () => {
    try {
      const terms = await apiService.getTerms();
      setNeedsTermsAcceptance(!terms.accepted);
    } catch {
      setNeedsTermsAcceptance(false);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await apiService.getMe();
        if (response) {
          setIsAuthenticated(true);
          setUser(response.user);
          setIsFirstLogin(response.user.first_login || false);

          await checkTermsAcceptance();

          try {
            const sub = await apiService.getSubscription();
            setSubscription(mapSubscription(sub));
          } catch {
            setSubscription(null);
          }
        } else {
          apiService.removeAuthToken();
        }
      } catch (error) {
        apiService.removeAuthToken();
        console.error("Auth check failed:", error);
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  // Disparado pelo apiService em qualquer 403 (ex.: trial expirou no backend).
  // Re-busca a assinatura; os guards de rota redirecionam para /subscribe ou
  // /account conforme o novo status. Se a assinatura ainda vale, nada muda.
  useEffect(() => {
    let refreshing = false;
    const refreshSubscription = async () => {
      if (refreshing) return;
      refreshing = true;
      try {
        const sub = await apiService.getSubscription();
        setSubscription(mapSubscription(sub));
      } catch {
        setSubscription(null);
      } finally {
        refreshing = false;
      }
    };
    window.addEventListener("subscription:refresh", refreshSubscription);
    return () =>
      window.removeEventListener("subscription:refresh", refreshSubscription);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError("");

      const response = await apiService.login({
        email,
        password,
        platform: "weedy",
      });

      if (response.token) {
        setSubscription(response.subscription || null);
        setNeedsTermsAcceptance(response.needsTermsAcceptance || false);
        setIsFirstLogin(response.firstLogin || false);

        try {
          const userResponse = await apiService.getMe();
          if (userResponse) {
            try {
              const sub = await apiService.getSubscription();
              setSubscription(mapSubscription(sub));
            } catch {
              setSubscription(null);
            }

            setIsAuthenticated(true);
            setUser(userResponse.user);
            setLoading(false);
            return true;
          } else {
            setError("Erro ao carregar dados do usuário");
            apiService.removeAuthToken();
            setLoading(false);
            return false;
          }
        } catch (userError) {
          setError("Erro ao carregar dados do usuário");
          apiService.removeAuthToken();
          setLoading(false);
          return false;
        }
      } else {
        setError("Credenciais inválidas");
        setLoading(false);
        return false;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Erro ao fazer login";
      setError(errorMessage);
      setLoading(false);
      return false;
    }
  };

  const acceptTerms = async (): Promise<boolean> => {
    try {
      const terms = await apiService.getTerms();
      await apiService.acceptTerms(terms.id);
      setNeedsTermsAcceptance(false);
      return true;
    } catch {
      return false;
    }
  };

  const logout = async () => {
    try {
      setLoading(true);

      try {
        await apiService.logout();
      } catch (error) {
        console.warn("Logout endpoint failed:", error);
      }

      apiService.removeAuthToken();
      apiService.removeRefreshToken();
      setIsAuthenticated(false);
      setUser(null);
      setSubscription(null);
      setNeedsTermsAcceptance(false);
      setIsFirstLogin(false);
      setError("");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearFirstLogin = () => {
    setIsFirstLogin(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isFirstLogin,
        login,
        logout,
        clearFirstLogin,
        loading,
        user,
        error,
        subscription,
        setSubscription,
        needsTermsAcceptance,
        acceptTerms,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
